import type { StandardTextFieldProps } from '@mui/material';
import { Stack } from '@mui/material';

import type { DateView } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers';
import type { FormikProps } from 'formik';
import { getIn } from 'formik';
import { Button } from './Button';
import { useCallback, useMemo, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { isBefore, isSameDay, parseISO, startOfDay, subDays } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { useLocale } from '@pflegenavi/frontend/localization';
import type { NestedKeysToObject } from './FTextInput';

interface IProps<T extends string, V> extends StandardTextFieldProps {
  id?: string;
  name: T;
  formik: FormikProps<V>;
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
  dataCy?: string;
  dataTestId?: string;
  openTo?: DateView;
}

export const FDatePicker = <
  FieldName extends string,
  V extends NestedKeysToObject<FieldName>
>({
  name,
  formik,
  minDate,
  maxDate,
  placeholder,
  label,
  disabled,
  dataCy,
  dataTestId,
  openTo = 'day',
  InputProps,
}: IProps<FieldName, V>): JSX.Element => {
  const isValidDate = (d: any) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return d instanceof Date && !isNaN(d);
  };

  const { t } = useTranslation();
  const locale = useLocale();

  const dateFormat = useMemo(
    () => (locale === 'de-AT' ? { format: 'dd.MM.yyyy' } : null),
    [locale]
  );

  const helperText = getIn(formik.touched, name)
    ? getIn(formik.errors, name)
    : undefined;
  const value = getIn(formik.values, name) ?? null;

  return (
    <DatePicker
      {...dateFormat}
      value={value}
      onChange={(selectedDate) => {
        if (selectedDate && isValidDate(selectedDate)) {
          formik.setFieldValue(
            name,
            fromZonedTime(startOfDay(selectedDate), 'UTC')
          );
          formik.setFieldError(name, undefined);
        } else if (selectedDate === null) {
          formik.setFieldValue(name, undefined);
          formik.setFieldError(name, undefined);
        } else {
          formik.setFieldError(name, t('field.valid-date'));
        }
      }}
      disabled={disabled || formik.isSubmitting}
      minDate={minDate}
      maxDate={maxDate}
      openTo={openTo}
      slotProps={{
        textField: {
          fullWidth: true,
          InputProps,
          label,
          onBlur: () => {
            formik.setFieldTouched(name);
          },
          name,
          error: Boolean(helperText),
          helperText: typeof helperText === 'string' ? helperText : '',
          slotProps: {
            htmlInput: {
              placeholder,
              'data-cy': dataCy,
              'data-testid': dataTestId,
            },
          },
        },
      }}
    />
  );
};

export const FDatePickerQuickActions = <
  Field extends string,
  V extends { [key in Field]?: Date }
>({
  name,
  formik,
  minDate,
  disabled,
}: IProps<Field, V>): JSX.Element | null => {
  const value = formik.values[name];

  const { setFieldValue } = formik;

  const setToday = useCallback(
    (date: Date) => {
      setFieldValue(name, date);
    },
    [setFieldValue, name]
  );

  const setYesterday = useCallback(
    (date: Date) => {
      setFieldValue(name, date);
    },
    [setFieldValue, name]
  );

  return (
    <DatePickerQuickActions
      name={name}
      value={value}
      setToday={setToday}
      setYesterday={setYesterday}
      minDate={minDate}
      disabled={disabled}
    />
  );
};

export const DatePickerQuickActions = ({
  name,
  value,
  disabled,
  setToday,
  setYesterday,
  minDate,
}: {
  name: string;
  value?: Date;
  disabled?: boolean;
  setToday: (date: Date) => void;
  setYesterday: (date: Date) => void;
  minDate?: Date;
}): JSX.Element | null => {
  const { t } = useTranslation();

  const getToday = () => fromZonedTime(startOfDay(new Date()), 'UTC');
  const getYesterday = useCallback(() => subDays(getToday(), 1), []);
  value = value && (value instanceof Date ? value : parseISO(value));
  const todayActive = value && isSameDay(value, getToday());
  const yesterdayActive = value && isSameDay(value, getYesterday());

  const setTodayCallback = useCallback(() => {
    setToday(getToday());
  }, [setToday]);

  const setYesterdayCallback = useCallback(() => {
    setYesterday(getYesterday());
  }, [setYesterday, getYesterday]);

  if (minDate && isBefore(getToday(), minDate)) {
    return null;
  }

  return (
    <Stack
      direction="row"
      sx={{
        gap: 0.5,
      }}
    >
      <Button
        name={name + 'QuickActionsToday'}
        tabIndex={-1}
        variant={
          todayActive ? (disabled ? 'outlined' : 'contained') : 'outlined'
        }
        color="primary"
        size="small"
        onClick={setTodayCallback}
        disabled={disabled}
      >
        {t('datePicker.today')}
      </Button>
      {(!minDate || !isBefore(getYesterday(), minDate)) && (
        <Button
          name={name + 'QuickActionsTomorrow'}
          tabIndex={-1}
          variant={
            yesterdayActive ? (disabled ? 'outlined' : 'contained') : 'outlined'
          }
          color="primary"
          size="small"
          onClick={setYesterdayCallback}
          disabled={disabled}
        >
          {t('datePicker.yesterday')}
        </Button>
      )}
    </Stack>
  );
};
